import axios from "axios";

const platform = process.env.REACT_APP_PLATFORM;
const isWindowsApp = platform === "windows";
const isAndroidApp = platform === "android";
const isPackagedApp = isWindowsApp || isAndroidApp;
const apiBaseURL = isPackagedApp
  ? process.env.REACT_APP_API_BASE_URL || "https://www.quicklearning-cmr.com/api/"
  : "/api/";
const defaultHeaders = {
  "Content-Type": "application/json",
  ...(!isPackagedApp && { "ngrok-skip-browser-warning": "true" }),
};

const api = axios.create({
  baseURL: apiBaseURL,
  headers: defaultHeaders,
});

const setAccessToken = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("access_token", accessToken);
  }
};

const getRefreshToken = () => localStorage.getItem("refresh_token");
let refreshPromise = null;

export const clearStoredTokens = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearStoredTokens();
    throw new Error("Refresh token absent.");
  }

  refreshPromise = axios.post(
    `${apiBaseURL}token/refresh/`,
    { refresh: refreshToken },
    { headers: defaultHeaders }
  ).then((response) => {
    const newAccessToken = response.data?.access;
    if (!newAccessToken) throw new Error("Access token absent de la réponse.");
    setAccessToken(newAccessToken);
    return newAccessToken;
  }).catch((error) => {
    // Sans réseau, le jeton reste : il resservira au retour de la connexion
    // (mode hors connexion). Seul un refus du serveur ferme la session.
    if (error?.response) clearStoredTokens();
    throw error;
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const PUBLIC_URLS = ["login/", "register/", "token/refresh/", "token/", "classes/", "manuels/", "slides/", "parent-teacher/config/", "parent-teacher/register/", "parent-teacher/login/", "parent-teacher/directory/"];

api.interceptors.request.use((config) => {
  const isPublic = PUBLIC_URLS.some(u => config.url?.startsWith(u));
  if (!isPublic) {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Convertit les URLs absolues localhost en URLs relatives
// afin que les images fonctionnent aussi via ngrok / HTTPS
function fixUrls(data) {
  if (!data || typeof data !== "object") return data;
  if (Array.isArray(data)) return data.map(fixUrls);
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === "string" && v.match(/^https?:\/\/localhost(:\d+)?\/media\//)) {
      out[k] = v.replace(/^https?:\/\/localhost(:\d+)?/, "");
    } else if (typeof v === "object" && v !== null) {
      out[k] = fixUrls(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

api.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      !(response.data instanceof Blob) &&
      !(response.data instanceof ArrayBuffer)
    ) {
      response.data = fixUrls(response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest.skipAuthRetry &&
      !originalRequest._retry &&
      !originalRequest.url.includes("token/")
    ) {
      originalRequest._retry = true;
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        if (isPackagedApp) clearStoredTokens();
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return await api(originalRequest);
      } catch (refreshError) {
        // Le refresh peut réussir (jeton toujours valide cryptographiquement)
        // tout en produisant un accès encore rejeté — cas d'une session fermée
        // à distance (connexion sur un autre appareil, déconnexion forcée).
        // Dans les deux cas, on nettoie et on force un rechargement pour que
        // l'appli réévalue l'état d'authentification (écran de connexion).
        if (!refreshError?.response) return Promise.reject(refreshError);
        clearStoredTokens();
        if (refreshError.response?.status === 401 && !window.__sessionSupersededReload) {
          window.__sessionSupersededReload = true;
          window.location.reload();
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
